import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react"

type Theme = "dark" | "light" | "system"
type ResolvedTheme = "dark" | "light"

type ThemeProviderProps = {
  children: React.ReactNode
  /** When set, the theme is controlled: it always resolves to this value,
   * ignoring the stored preference and OS setting. The PDF routes pass "light"
   * so they render the same regardless of the viewer. */
  theme?: Theme
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  resolvedTheme: ResolvedTheme
  setTheme: (theme: Theme) => void
}

const isTheme = (value: string | null): value is Theme =>
  value === "dark" || value === "light" || value === "system"

const darkModeQuery = "(prefers-color-scheme: dark)"

const subscribeToSystem = (onChange: () => void) => {
  const mq = window.matchMedia(darkModeQuery)
  mq.addEventListener("change", onChange)
  return () => mq.removeEventListener("change", onChange)
}

const applyThemeClass = (resolved: ResolvedTheme) => {
  const root = window.document.documentElement
  root.classList.remove("light", "dark")
  if (resolved === "dark") {
    root.classList.add("dark")
  }
  root.classList.add("font-pelikan")
}

const initialState: ThemeProviderState = {
  theme: "light",
  resolvedTheme: "light",
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  theme: controlledTheme,
  defaultTheme = "light",
  storageKey = "lumen-ui-theme",
}: ThemeProviderProps) {
  const [storedTheme, setStoredTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(storageKey)
    return isTheme(stored) ? stored : defaultTheme
  })

  const systemPrefersDark = useSyncExternalStore(
    subscribeToSystem,
    () => window.matchMedia(darkModeQuery).matches,
  )

  // The controlled prop always wins. Otherwise use the stored preference.
  const theme = controlledTheme ?? storedTheme
  const resolvedTheme: ResolvedTheme =
    theme === "system" ? (systemPrefersDark ? "dark" : "light") : theme

  // Apply the class during render, not in an effect: the provider renders
  // before its descendants, so charts reading CSS variables via getCssVar see
  // the current theme on this render instead of a frame late.
  applyThemeClass(resolvedTheme)

  const setTheme = useCallback(
    (next: Theme) => {
      localStorage.setItem(storageKey, next)
      setStoredTheme(next)
    },
    [storageKey],
  )

  const value = useMemo<ThemeProviderState>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  )

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)

  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")

  return context
}
